import type { TestimonialsData, Testimonial, ReviewLink } from '@/data/types';
import testimonialsData from '@/data/testimonials.json';

interface TestimonialCardProps {
  testimonial: Testimonial;
}

function TestimonialCard({ testimonial }: TestimonialCardProps) {
  return (
    <div className="mb-8">
      <div className="bg-gray-50 p-8 rounded-lg shadow-sm">
        <div className="flex items-start text-left">
          {/* Quote icon */}
          <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-6 flex-shrink-0">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h4v10h-10z" fill="white"/>
            </svg>
          </div>
          
          <div className="flex-1">
            {/* Testimonial content */}
            <p className="text-gray-700 text-base leading-relaxed mb-6">
              {testimonial.content}
            </p>
            
            {/* Author info */}
            <div>
              <h4 className="font-semibold text-gray-900 mb-1">
                {testimonial.author}
              </h4>
              <p className="text-gray-600 text-sm">
                {testimonial.company}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ReviewButtonProps {
  reviewLink: ReviewLink;
}

function ReviewButton({ reviewLink }: ReviewButtonProps) {
  return (
    <a
      href={reviewLink.href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center justify-center px-8 py-3 text-sm font-medium text-white bg-primary hover:bg-primary-dark transition-colors duration-200 rounded shadow hover:shadow-md"
    >
      {reviewLink.label.toUpperCase()}
    </a>
  );
}

export function Testimonials() {
  const { testimonials, additionalReviews }: TestimonialsData = testimonialsData;

  return (
    <section className="bg-gray-100 py-16">
      <div className="max-w-6xl mx-auto px-4">
        {/* Client Reviews heading */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            Client Reviews
          </h2>
        </div>
        
        {/* Testimonials */}
        <div className="space-y-8 mb-16">
          {testimonials.map((testimonial) => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>
        
        {/* Additional Reviews section */}
        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">
            Additional Reviews
          </h3>
          
          {/* Review buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            {additionalReviews.map((reviewLink) => (
              <ReviewButton key={reviewLink.id} reviewLink={reviewLink} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}